import { useId, useState } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { styles } from './tooltip.styles';
interface TooltipProps {
  /** The explanation. One or two sentences — this is a definition, not a manual. */
  text: string;
  /** Accessible name for the trigger. Defaults to a generic "What is this?", but
   *  a form with twenty of them should name the field so a screen-reader user
   *  hears "About photoperiod" rather than twenty identical buttons. */
  label?: string | undefined;
  /** Open leftwards. Set on the last field in a row so the bubble does not
   *  overflow the panel it sits in. */
  alignEnd?: boolean | undefined;
}

/**
 * A small "?" that explains one field.
 *
 * **Deliberately not hover-only.** The console runs on a tablet in a grow room
 * where there is no hover at all, so the trigger is a real `<button>` that opens
 * on CLICK and on focus, and closes on Escape or blur. Hover is added on top for
 * a mouse, not relied on. A `title` attribute was the cheaper option and was
 * rejected for the same reason: it never appears on touch, and it cannot be
 * reached by keyboard.
 *
 * The bubble is `aria-describedby`-linked to the trigger rather than being an
 * `aria-label`, because the explanation is supplementary — a screen reader
 * should read the field's own label first and this only if asked.
 */
export function Tooltip({
  text,
  label,
  alignEnd = false
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return <span {...stylex.props(styles.wrap)} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" aria-label={label != null ? `About ${label}` : 'What is this?'} aria-expanded={open} aria-describedby={open ? id : undefined} {...stylex.props(styles.trigger)} onClick={() => setOpen(v => !v)} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onKeyDown={e => {
      if (e.key === 'Escape' && open) {
        // Stop here: inside a modal this would otherwise close the whole
        // dialog, losing the operator's unsaved edit to dismiss a tooltip.
        e.stopPropagation();
        setOpen(false);
      }
    }}>
        ?
      </button>
      {open && <span id={id} role="tooltip" {...stylex.props(styles.bubble, alignEnd && styles.bubbleEnd)}>
          {text}
        </span>}
    </span>;
}